// src/components/ui/Table/ResizeHint.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './ResizeHint.module.css';

interface ResizeHintProps {
  tableId: string; // Идентификатор таблицы для сохранения настройки скрытия подсказки
  onDismiss?: () => void; // Опциональный колбэк при закрытии подсказки
}

export const ResizeHint: React.FC<ResizeHintProps> = ({ tableId, onDismiss }) => {
  const [dismissed, setDismissed] = useState(false);
  const storageKey = `table_resize_hint_dismissed_${tableId}`;
  
  // Используем хук для переводов
  const { t } = useTranslation('ui');
  
  // Используем хук для адаптивности
  const { isMobile, isTablet } = useResponsive();
  
  // Определяем, нужно ли отображать подсказку на мобильных устройствах
  // (На мобильных нет смысла показывать подсказку о изменении размеров)
  const shouldShowForDevice = !isMobile;

  // Проверяем, закрывал ли пользователь подсказку ранее
  useEffect(() => {
    const hintDismissed = localStorage.getItem(storageKey) === 'true';
    setDismissed(hintDismissed);
  }, [storageKey]);

  // Обработчик закрытия подсказки
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(storageKey, 'true');
    if (onDismiss) {
      onDismiss();
    }
  };

  // Не отображаем подсказку, если она закрыта или устройство не подходит
  if (dismissed || !shouldShowForDevice) {
    return null;
  }

  return (
    <div className={`${styles.container} ${isTablet ? styles.containerTablet : styles.containerDesktop}`}>
      <div className={styles.content}>
        <div className={styles.iconContainer}>
          <svg 
            className={`${styles.icon} ${isTablet ? styles.iconTablet : styles.iconDesktop}`} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
              clipRule="evenodd" 
            />
          </svg>
        </div>
        <div className={styles.textContainer}>
          <h3 className={`${styles.title} ${isTablet ? styles.titleTablet : styles.titleDesktop}`}>
            {t('table.resize.title')}
          </h3>
          <div className={`${styles.description} ${isTablet ? styles.descriptionTablet : styles.descriptionDesktop}`}>
            <p>{t('table.resize.description')}</p>
            <ul className={styles.list}>
              <li>{t('table.resize.step1')}</li>
              <li>{t('table.resize.step2')}</li>
              <li>{t('table.resize.step3')}</li>
              <li>{t('table.resize.step4')}</li>
            </ul>
          </div>
        </div>
      </div>
      <button 
        className={styles.closeButton} 
        onClick={handleDismiss}
        aria-label={t('table.resize.close')}
      >
        <svg 
          className={isTablet ? styles.closeIconTablet : styles.closeIconDesktop} 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path 
            fillRule="evenodd" 
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" 
            clipRule="evenodd" 
          />
        </svg>
      </button>
    </div>
  );
};

export default ResizeHint;